import { ActionIcon, Group, Indicator, Menu, Text } from '@mantine/core'
import { IconTerminal2 } from '@tabler/icons-react'

import { moverJanela, pinar, useSsmStore, type SessaoNaTela } from '@/store/ssm'

/**
 * Quantos shells esta janela do navegador tem abertos, e como voltar a eles.
 *
 * ## Por que existe
 *
 * **Não é enfeite: é o que substitui o fusível que a TASK-10345 removeu.** Enquanto
 * as sessões morriam ao desmontar a tela de Servidores, sair da tela bastava para
 * não haver shell esquecido. Agora elas sobrevivem à navegação — e sem nada aqui,
 * uma janela atrás de um modal, fora da área visível ou coberta pelo navegador vira
 * shell root vivo em servidor de cliente que ninguém está vendo.
 *
 * ## O número é o DESTE navegador, e ele diz isso
 *
 * A tela de Servidores mostra outro contador, "N de 6", que vem do servidor e conta
 * as sessões da pessoa em qualquer aba ou dispositivo — é ele que gateia o teto.
 * Este conta as locais, porque são as únicas que ele pode trazer de volta à vista.
 *
 * Os dois divergem legitimamente (outra aba, outro dispositivo, o app de desktop), e
 * é por isso que cada um diz o que é. Dois números na mesma tela sem dizer o que
 * medem é a forma exata de divergência que custou caro em 01/09/2026.
 */

/** Posição de resgate: canto conhecido, dentro da área visível. */
function posicaoDeResgate(indice: number) {
  const w = 620
  const h = 380
  return {
    w,
    h,
    x: Math.max(0, Math.min(window.innerWidth - w, 120 + indice * 28)),
    y: Math.max(0, Math.min(window.innerHeight - 80, 120 + indice * 28)),
  }
}

export function IndicadorDeShells() {
  const sessoes = useSsmStore(e => e.sessoes)
  const lista = Object.values(sessoes) as SessaoNaTela[]

  if (lista.length === 0) return null

  const trazerParaAVista = (s: SessaoNaTela, i: number) => {
    if (s.modo !== 'flutuante') pinar(s.instanceId)
    // Reposiciona SEMPRE, e não só ao pinar: o caso que dói é a janela ter ficado
    // fora da área visível — outro monitor, a tela encolheu, a pessoa arrastou
    // para o canto. Trazer "para a frente" sem trazer para dentro não resolveria.
    moverJanela(s.instanceId, posicaoDeResgate(i))
  }

  return (
    <Menu shadow="md" width={300} position="bottom-end">
      <Menu.Target>
        <Indicator label={String(lista.length)} size={16} color="teal" offset={4}>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="md"
            aria-label={`${lista.length} shell(s) aberto(s) nesta janela do navegador`}
            title={`${lista.length} shell(s) aberto(s) nesta janela do navegador`}
          >
            <IconTerminal2 size={18} />
          </ActionIcon>
        </Indicator>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>Shells abertos nesta janela</Menu.Label>
        {lista.map((s, i) => (
          <Menu.Item key={s.instanceId} onClick={() => trazerParaAVista(s, i)}>
            <Group gap={6} wrap="nowrap">
              <Text size="sm" truncate>{s.nome}</Text>
              <Text size="xs" c="dimmed" truncate>· {s.tenantNome}</Text>
            </Group>
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  )
}
